import { useState, useRef } from 'react';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Coerce incoming value (plain strings or {email,label,enabled}) to objects. */
export function toRecipientObjects(value) {
  return (value || [])
    .map((r) =>
      typeof r === 'string'
        ? { email: r.trim(), label: '', enabled: true }
        : { email: String(r?.email || '').trim(), label: String(r?.label || ''), enabled: r?.enabled !== false }
    )
    .filter((r) => r.email);
}

/**
 * EmailRecipients — manage who receives approval & digest emails for a project.
 *
 * Type an address and press Enter (or Add); it becomes a row with a pause
 * toggle, an optional label, and a remove control. Value is the canonical
 * [{ email, label, enabled }] shape the API stores, so it round-trips cleanly
 * through the Brand Brief and the New Project wizard alike.
 *
 * A pending draft is also committed when focus leaves the whole control (so a
 * typed-but-not-Entered address isn't silently lost when the owner clicks
 * "Save brief" without pressing Enter first) — mirroring ChipInput. Blur is
 * ignored when focus moves to a button inside the control (Add / pause /
 * remove handle their own actions). The draft accepts a comma / space /
 * newline separated list too, so a block of addresses can be pasted at once.
 */
export function EmailRecipients({ value, onChange }) {
  const list = toRecipientObjects(value);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const containerRef = useRef(null);

  /**
   * Parse one-or-many addresses out of `raw`, add the valid+new ones, and
   * surface any invalid entries. Returns true when at least one address was
   * added (so the caller can clear the draft).
   */
  const commitDraft = (raw) => {
    const candidates = String(raw)
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    if (!candidates.length) { setError('Enter an email address first.'); return false; }

    const seen = new Set(list.map((r) => r.email.toLowerCase()));
    const toAdd = [];
    const invalid = [];
    for (const email of candidates) {
      if (!EMAIL_RE.test(email)) { invalid.push(email); continue; }
      if (seen.has(email)) continue; // already on the list or duplicated in this paste
      seen.add(email);
      toAdd.push({ email, label: '', enabled: true });
    }

    if (toAdd.length) onChange([...list, ...toAdd]);
    if (invalid.length) {
      setError(invalid.length === 1
        ? `“${invalid[0]}” doesn't look like an email address.`
        : `${invalid.length} entries weren't valid email addresses.`);
    } else {
      setError('');
    }
    return toAdd.length > 0;
  };

  const add = () => { if (commitDraft(draft)) setDraft(''); };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); }
  };

  // Commit a typed-but-not-added address when focus leaves the whole control
  // (skip when focus moves to a button inside it — those handle themselves).
  const onBlur = (e) => {
    if (draft.trim() && !containerRef.current?.contains(e.relatedTarget)) {
      if (commitDraft(draft)) setDraft('');
    }
  };

  const update = (i, patch) => onChange(list.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div className="recipients" ref={containerRef}>
      <div className="recipients-add">
        <input
          type="email"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          aria-label="Add notification email"
          placeholder="name@company.com"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (error) setError(''); }}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
        />
        <button type="button" className="btn-outline" onClick={add}>Add</button>
      </div>

      {error && <div className="recipients-error" role="alert">{error}</div>}

      {list.length > 0 ? (
        <ul className="recipients-list">
          {list.map((r, i) => (
            <li key={r.email} className={`recipient-row${r.enabled ? '' : ' is-off'}`}>
              <button
                type="button"
                className={`recipient-toggle${r.enabled ? ' on' : ''}`}
                aria-pressed={r.enabled}
                aria-label={r.enabled ? `Pause emails to ${r.email}` : `Resume emails to ${r.email}`}
                title={r.enabled ? 'Receiving emails — click to pause' : 'Paused — click to resume'}
                onClick={() => update(i, { enabled: !r.enabled })}
              >
                <span className="recipient-toggle-dot" />
              </button>
              <span className="recipient-email" title={r.email}>{r.email}</span>
              <input
                className="recipient-label"
                placeholder="label (optional)"
                aria-label={`Label for ${r.email} (optional)`}
                value={r.label}
                onChange={(e) => update(i, { label: e.target.value })}
              />
              <button type="button" className="recipient-remove" aria-label={`Remove ${r.email}`} onClick={() => remove(i)}>×</button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="recipients-empty">No one yet — add an address (or paste several, comma- or space-separated) to receive approval &amp; digest emails.</p>
      )}
    </div>
  );
}
